import { useState, useEffect, useCallback } from 'react'
import { useAuth } from './useAuth'
import { budgetService } from '../services/budgetService'
import { AIBudget } from '../types/ai-budget'

interface UseBudgetsReturn {
  budgets: AIBudget[]
  loading: boolean
  error: string | null
  refreshBudgets: () => Promise<void>
  deleteBudget: (budgetId: string) => Promise<void>
  updateBudgetStatus: (budgetId: string, status: 'active' | 'paused' | 'completed') => Promise<void>
}

export const useBudgets = (): UseBudgetsReturn => {
  const { user } = useAuth()
  const [budgets, setBudgets] = useState<AIBudget[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch user budgets
  const fetchBudgets = useCallback(async () => {
    if (!user) {
      setBudgets([])
      setLoading(false)
      return
    }

    console.log('📊 useBudgets: Fetching budgets for user:', user.id)
    
    try {
      setLoading(true)
      setError(null)
      
      const userBudgets = await budgetService.getUserBudgets(user.id)
      
      console.log(`📊 useBudgets: Found ${userBudgets.length} budgets`)
      setBudgets(userBudgets)
    } catch (err) {
      console.error('❌ useBudgets: Error fetching budgets:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch budgets'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [user])

  // Refresh budgets
  const refreshBudgets = useCallback(async () => {
    await fetchBudgets()
  }, [fetchBudgets])

  // Delete a budget
  const deleteBudget = useCallback(async (budgetId: string) => {
    console.log('🗑️ useBudgets: Deleting budget:', budgetId)
    
    try {
      setError(null)
      
      await budgetService.deleteBudget(budgetId)
      
      // Remove from local state
      setBudgets(prev => prev.filter(b => b.id !== budgetId))
      
      console.log('✅ useBudgets: Budget deleted successfully')
    } catch (err) {
      console.error('❌ useBudgets: Error deleting budget:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete budget'
      setError(errorMessage)
      throw err
    }
  }, [])

  // Update budget status
  const updateBudgetStatus = useCallback(async (budgetId: string, status: 'active' | 'paused' | 'completed') => {
    console.log(`🔄 useBudgets: Updating budget ${budgetId} status to:`, status)
    
    try {
      setError(null)
      
      await budgetService.updateBudgetStatus(budgetId, status)
      
      // Update local state
      setBudgets(prev => prev.map(b => 
        b.id === budgetId ? { ...b, status, updated_at: new Date().toISOString() } : b
      ))
      
      console.log('✅ useBudgets: Budget status updated successfully')
    } catch (err) {
      console.error('❌ useBudgets: Error updating budget status:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to update budget status'
      setError(errorMessage)
      throw err
    }
  }, [])

  // Fetch budgets when user changes
  useEffect(() => {
    fetchBudgets()
  }, [fetchBudgets])

  return {
    budgets,
    loading,
    error,
    refreshBudgets,
    deleteBudget,
    updateBudgetStatus
  }
}





